import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { toLocalYmd } from '../pages/analiseBots/utils/formatters';

/** Tipos de bot do monitoramento (era exportado pelo useAnaliseBots, removido). */
export type TipoBot = 'RPA' | 'ETL' | 'WEBSCRAP';

export interface ILogBot {
  id?: string;
  bot_name: string;
  exec_id: string;
  bot_flag: 'RPA' | 'ETL' | 'WEBSCRAP';
  flag: 'INFO' | 'WARNING' | 'ERROR';
  content_line: string;
  codi_emp?: string | null;
  timestamp: string;
  date_init?: string | null;
  date_end?: string | null;
  step?: string | null;
  data?: Record<string, any> | null;
  screenshot_url?: string | null;
}

// Interface para a resposta da API (estrutura original)
interface ILogBotAPI {
  id?: string;
  ts: string;
  meta: {
    bot: string;
  };
  exec_id: string;
  flag: 'INFO' | 'WARNING' | 'ERROR';
  content_line: string;
  codi_emp?: string | null;
  date_init?: string | null;
  date_end?: string | null;
  step?: string | null;
  data?: Record<string, any> | null;
  screenshot_url?: string | null;
}

interface IMonitoramentoResponseAPI {
  status: string;
  message: string;
  data: ILogBotAPI[];
  pagination: IMonitoramentoPagination;
}

export interface IMonitoramentoPagination {
  page: number;
  size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface IMonitoramentoResponse {
  status: string;
  message: string;
  data: ILogBot[];
  pagination: IMonitoramentoPagination;
}

export interface IMonitoramentoFilters {
  page?: number;
  size?: number;
  bot_name?: string;
  exec_id?: string;
  bot_flag?: 'ETL' | 'RPA' | 'WEBSCRAP';
  flag?: 'INFO' | 'WARNING' | 'ERROR';
  content_line?: string;
  codi_emp?: string;
  step?: string;
  date_init?: string;
  date_end?: string;
  timestamp_init?: string;
  timestamp_end?: string;
}

export interface IExecucao {
  exec_id: string;
  date_init: string | null;
  date_end: string | null;
  first_flag: string | null;
  last_flag: string | null;
  last_content: string;
  last_ts: string | null;
  total_logs: number;
  total_errors: number;
  total_warnings: number;
  status: 'ok' | 'warning' | 'error';
  falhas: number;
  total_empresas: number;
  total_empresas_erro: number;
  total_empresas_warning: number;
}

export interface IBotsResponse {
  status: string;
  message: string;
  data: string[];
}

export interface IExecucoesPagination {
  page: number;
  size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface IExecucoesData {
  data: IExecucao[];
  pagination: IExecucoesPagination;
}

export interface IExecucoesResponse {
  status: string;
  message: string;
  data: IExecucoesData;
}

export const useMonitoramento = () => {
  // Função para transformar os dados da API para o formato esperado
  const transformLogData = (apiLog: ILogBotAPI, botFlag: 'RPA' | 'ETL' | 'WEBSCRAP'): ILogBot => {
    return {
      id: apiLog.id,
      bot_name: apiLog.meta.bot,
      exec_id: apiLog.exec_id,
      bot_flag: botFlag,
      flag: apiLog.flag,
      content_line: apiLog.content_line,
      codi_emp: apiLog.codi_emp,
      timestamp: apiLog.ts,
      date_init: apiLog.date_init,
      date_end: apiLog.date_end,
      step: apiLog.step,
      data: apiLog.data,
      screenshot_url: apiLog.screenshot_url,
    };
  };

  const getLogsRPA = async (key: QueryFunctionContext): Promise<IMonitoramentoResponse> => {
    const filters = (key.queryKey?.[1] as IMonitoramentoFilters) ?? {};
    const res = await api.get<IMonitoramentoResponseAPI>('/api/v1/orquestrador/logs/listar/RPA', {
      params: filters,
    });

    return {
      status: res.data.status,
      message: res.data.message,
      data: res.data.data.map(log => transformLogData(log, 'RPA')),
      pagination: res.data.pagination,
    };
  };

  const getLogsETL = async (key: QueryFunctionContext): Promise<IMonitoramentoResponse> => {
    const filters = (key.queryKey?.[1] as IMonitoramentoFilters) ?? {};
    const res = await api.get<IMonitoramentoResponseAPI>('/api/v1/orquestrador/logs/listar/ETL', {
      params: filters,
    });

    return {
      status: res.data.status,
      message: res.data.message,
      data: res.data.data.map(log => transformLogData(log, 'ETL')),
      pagination: res.data.pagination,
    };
  };

  const getLogsWebScrap = async (key: QueryFunctionContext): Promise<IMonitoramentoResponse> => {
    const filters = (key.queryKey?.[1] as IMonitoramentoFilters) ?? {};
    const res = await api.get<IMonitoramentoResponseAPI>(
      '/api/v1/orquestrador/logs/listar/WEBSCRAP',
      {
        params: filters,
      },
    );

    return {
      status: res.data.status,
      message: res.data.message,
      data: res.data.data.map(log => transformLogData(log, 'WEBSCRAP')),
      pagination: res.data.pagination,
    };
  };

  const getBotsRPA = async (): Promise<string[]> => {
    const res = await api.get<IBotsResponse>('/api/v1/orquestrador/logs/bots/RPA');
    return res.data.data;
  };

  const getBotsETL = async (): Promise<string[]> => {
    const res = await api.get<IBotsResponse>('/api/v1/orquestrador/logs/bots/ETL');
    return res.data.data;
  };

  const getBotsWebScrap = async (): Promise<string[]> => {
    const res = await api.get<IBotsResponse>('/api/v1/orquestrador/logs/bots/WEBSCRAP');
    return res.data.data;
  };

  // Normaliza resposta: suporta formato antigo (array) e novo (objeto com pagination)
  const normalizeExecResponse = (raw: any): IExecucoesData => {
    if (Array.isArray(raw)) {
      return {
        data: raw,
        pagination: {
          page: 1,
          size: raw.length,
          total: raw.length,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        },
      };
    }
    return raw as IExecucoesData;
  };

  const getExecucoesRPA = async (botName: string, page = 1, size = 20): Promise<IExecucoesData> => {
    const res = await api.get(
      `/api/v1/orquestrador/logs/execucoes/RPA/${encodeURIComponent(botName)}`,
      { params: { page, size } },
    );
    return normalizeExecResponse(res.data.data);
  };

  const getExecucoesETL = async (botName: string, page = 1, size = 20): Promise<IExecucoesData> => {
    const res = await api.get(
      `/api/v1/orquestrador/logs/execucoes/ETL/${encodeURIComponent(botName)}`,
      { params: { page, size } },
    );
    return normalizeExecResponse(res.data.data);
  };

  const getExecucoesWebScrap = async (
    botName: string,
    page = 1,
    size = 20,
  ): Promise<IExecucoesData> => {
    const res = await api.get(
      `/api/v1/orquestrador/logs/execucoes/WEBSCRAP/${encodeURIComponent(botName)}`,
      { params: { page, size } },
    );
    return normalizeExecResponse(res.data.data);
  };

  const exportarLogsCSV = async (
    tipoBot: 'RPA' | 'ETL' | 'WEBSCRAP',
    filters?: IMonitoramentoFilters,
  ): Promise<void> => {
    const params: Record<string, any> = {};

    if (filters?.bot_name) params.bot_name = filters.bot_name;
    if (filters?.flag) params.flag = filters.flag;
    if (filters?.exec_id) params.exec_id = filters.exec_id;
    if (filters?.content_line) params.content_line = filters.content_line;
    if (filters?.codi_emp) params.codi_emp = filters.codi_emp;
    if (filters?.step) params.step = filters.step;
    if (filters?.date_init) params.date_init = filters.date_init;
    if (filters?.date_end) params.date_end = filters.date_end;

    try {
      const res = await api.get(`/api/v1/orquestrador/logs/exportar/${tipoBot}`, {
        params,
        responseType: 'blob',
      });

      // Verificar se a resposta é um erro (JSON em vez de CSV)
      if (res.data.type === 'application/json') {
        const text = await res.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail?.message || 'Erro ao exportar');
      }

      // Extrair nome do arquivo do header ou gerar um padrão
      const contentDisposition = res.headers['content-disposition'];
      let filename = `logs_${tipoBot}_${toLocalYmd(new Date())}.csv`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }

      // Criar blob e fazer download
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      // Se o erro for um blob, tentar ler como texto
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail?.message || errorData.message || 'Erro ao exportar');
        } catch {
          throw new Error(text || 'Erro ao exportar');
        }
      }
      throw error;
    }
  };

  return {
    getLogsRPA,
    getLogsETL,
    getLogsWebScrap,
    getBotsRPA,
    getBotsETL,
    getBotsWebScrap,
    getExecucoesRPA,
    getExecucoesETL,
    getExecucoesWebScrap,
    exportarLogsCSV,
  };
};
